import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import CTA from "../components/cta";

const PAGE_SIZE = 4;

function Blogs() {
  const [listings, setListings] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [sortBy] = useState("id DESC");
  const [selectedAuthor, setSelectedAuthor] = useState("All");
  const API =
    process.env.REACT_APP_API_URL || "https://nvrealestate.websiteseoonline.com";

  const fetchListings = useCallback(async (pageNo, isLoadMore = false) => {
    setLoading(true);

    try {
      const res = await fetch(`${API}/api/blogsall.ashx`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          page: pageNo,
          pageSize: PAGE_SIZE,
          sortBy,
        }),
      });

      const result = await res.json();

      if (result.result === 1) {
        setListings((prev) =>
          isLoadMore ? [...prev, ...(result.data || [])] : result.data || []
        );

        setHasMore(pageNo * PAGE_SIZE < (result.total || 0));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [API, sortBy]);

  useEffect(() => {
    fetchListings(1, false);
  }, [fetchListings]);

  const authors = useMemo(() => {
    const authorNames = listings
      .map((item) => getAuthor(item))
      .filter(Boolean);

    return ["All", ...Array.from(new Set(authorNames))];
  }, [listings]);

  const filteredListings = useMemo(() => {
    if (selectedAuthor === "All") {
      return listings;
    }

    return listings.filter((item) => getAuthor(item) === selectedAuthor);
  }, [listings, selectedAuthor]);

  const handleLoadMore = () => {
    const nextPage = page + 1;
    setPage(nextPage);
    fetchListings(nextPage, true);
  };

  return (
    <>
      <section className="blog-news-page">
        <div className="blog-news-container pt-20">
         

          <div className="blog-intro-wrap">
            <span className="blog-dot-grid" aria-hidden="true" />
            <div className="blog-intro-card">
              <h1>Dubai Real Estate News</h1>
              <p>
               Stay informed with the latest updates and emerging trends in Dubai’s real estate market through Nova Vision Real Estate. Discover new developments, market insights, and innovative opportunities shaping the city’s dynamic property landscape. With Nova Vision Real Estate, you stay ahead in one of the world’s most fast-moving and vibrant property markets.
              </p>
            </div>
          </div>
 

          <div className="blog-list">
            {filteredListings.map((item, index) => {
              const author = getAuthor(item);
              const agentUrl = item.agentUrl || item.AgentUrl || "";
              const agentImage = item.agentImage || item.AgentImage || "";
              const date = formatDate(item.date || item.createddate || item.Date);
              const views = item.views || item.viewcount || item.ViewCount || 182;

              return (
                <article className="blog-list-item" key={item.id || index}>
                  <Link className="blog-list-image" to={item.url}>
                    <img
                      src={`${item.Image}/w=620,sharpen=1`}
                      alt={item.title}
                      loading={index === 0 ? "eager" : "lazy"}
                    />
                  </Link>

                  <div className="blog-list-content">
                    <Link className="blog-title-link" to={item.url}>
                      <h2>{item.title}</h2>
                    </Link>

                    <div className="blog-meta">
                      {agentUrl ? (
                        <Link
                          className="blog-author-link"
                          to={agentUrl}
                          aria-label={`View ${author}'s profile`}
                        >
                          {agentImage ? (
                            <img
                              className="blog-author-image"
                              src={`${agentImage}/w=100,h=100,fit=cover,sharpen=1`}
                              alt={author}
                              loading="lazy"
                            />
                          ) : (
                            <span className="blog-author-mark">
                              {getInitials(author)}
                            </span>
                          )}
                        </Link>
                      ) : (
                        <span className="blog-author-mark">NV</span>
                      )}
                      <span>By</span>
                      {agentUrl ? (
                        <Link to={agentUrl}>{author}</Link>
                      ) : (
                        <span className="blog-author-fallback">{author}</span>
                      )}
                      {date && <span>on {date}</span>}
                    </div>

                    <p>{item.shortdescription}</p>

                    <div className="blog-list-footer">
                      <Link className="blog-read-more" to={item.url}>
                        Read More <i className="fas fa-share" />
                      </Link>
 
                    </div>
                  </div>
                </article>
              );
            })}
          </div>

          {filteredListings.length === 0 && !loading && (
            <p className="blog-empty">No blog posts found.</p>
          )}

          {hasMore && (
            <div className="blog-load-more">
              <button
                className="tf-btn-full btn-line"
                onClick={handleLoadMore}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2" />
                    Loading...
                  </>
                ) : (
                  "Load More"
                )}
              </button>
            </div>
          )}
        </div>
      </section>

      <style
        dangerouslySetInnerHTML={{
          __html: `
            .blog-news-page {
              background: #fff;
              color: #141414;
              padding: 16px 20px 72px;
            }

            .blog-news-container {
              max-width: 1108px;
              margin: 0 auto;
            }

            .blog-breadcrumb {
              display: flex;
              align-items: center;
              gap: 10px;
              color: #000;
              font-size: 14px;
              margin-bottom: 44px;
            }

            .blog-breadcrumb a {
              color: #0d6efd;
              line-height: 1;
            }

            .blog-intro-wrap {
              position: relative;
              margin-bottom: 38px;
            }

            .blog-dot-grid {
              position: absolute;
              left: -32px;
              top: -26px;
              width: 90px;
              height: 90px;
              background-image: radial-gradient(#c9dbff 2px, transparent 2px);
              background-size: 20px 20px;
              pointer-events: none;
            }

            .blog-intro-card {
              position: relative;
              border: 1px solid #dedede;
              border-radius: 14px;
              padding: 28px 30px 46px;
              background: #fff;
            }

            .blog-intro-card h1 {
              margin: 0 0 18px;
              color: #050505; 
              font-size: 34px;
              font-weight: 500;
              letter-spacing: 0;
              line-height: 1.2;
            }

            .blog-intro-card p {
              margin: 0;
              max-width: 1040px;
              color: #1f1f1f;
              font-size: 15px;
              line-height: 1.72;
            }

            .blog-filter-row {
              display: flex;
              align-items: center;
              gap: 16px;
              margin: 0 0 44px 27px;
            }

            .blog-filter-row label {
              color: #363636;
              font-size: 16px;
              margin: 0;
            }

            .blog-filter-row select {
              width: 174px;
              height: 40px;
              border: 1px solid #dedede;
              border-radius: 8px;
              color: #333;
              background: #fafafa;
              padding: 0 12px;
              font-size: 14px;
            }

            .blog-list {
              display: flex;
              flex-direction: column;
            }

            .blog-list-item {
              display: grid;
              grid-template-columns: 442px minmax(0, 1fr);
              gap: 20px;
              border-bottom: 1px solid #e2e2e2;
              padding: 0 0 20px;
              margin-bottom: 20px;
            }

            .blog-list-image {
              display: block;
              overflow: hidden;
              border-radius: 12px;
              aspect-ratio: 442 / 296;
              background: #f0f0f0;
            }

            .blog-list-image img {
              width: 100%;
              height: 100%;
              display: block;
              object-fit: cover;
              transition: transform 0.3s ease;
            }

            .blog-list-image:hover img {
              transform: scale(1.04);
            }

            .blog-list-content {
              min-width: 0;
              padding-top: 13px;
            }

            .blog-title-link {
              text-decoration: none;
            }

            .blog-list-content h2 {
             margin: 0 0 20px;
    color: #273884;
    font-size: 20px;
    font-weight: 600;
    letter-spacing: 0;
    line-height: 1.24;
            }

            .blog-title-link:hover h2 {
              color: #273884;
            }

            .blog-meta {
              display: flex;
              align-items: center;
              flex-wrap: wrap;
              gap: 5px;
              color: #777;
              font-size: 14px;
              margin-bottom: 24px;
            }

            .blog-author-mark {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              width: 40px;
              height: 40px;
              margin-right: 7px;
              border-radius: 50%;
              background: #273884;
              color: #fff;
              font-size: 14px;
              font-weight: 800;
              text-transform: uppercase;
            }

            .blog-author-link {
              display: inline-flex;
              margin-right: 7px;
              border-radius: 50%;
              flex: 0 0 auto;
            }

            .blog-author-link .blog-author-mark {
              margin-right: 0;
            }

            .blog-author-image {
              display: block;
              width: 40px;
              height: 40px;
              border-radius: 50%;
              object-fit: cover;
              background: #f0f0f0;
            }

            .blog-author-fallback {
              color: #273884;
              font-weight: 500;
            }

            .blog-meta a {
              color: #273884;
              text-decoration: none;
              font-weight: 500;
            }

            .blog-list-content p {
              margin: 0;
              max-width: 640px;
              color: #777;
              font-size: 15px;
              line-height: 1.28;
            }

            .blog-list-footer {
              display: flex;
              align-items: center;
              justify-content: space-between;
              gap: 16px;
              margin-top: 45px;
            }

            .blog-read-more {
              display: inline-flex;
              align-items: center;
              gap: 8px;
              min-height: 40px;
              border: 1px solid #273884;
              border-radius: 22px;
              padding: 8px 12px;
              color: #273884;
              font-size: 14px;
              line-height: 1;
              text-decoration: none;
              transition: all 0.2s ease;
            }

            .blog-read-more i {
              color: #273884;
            }

            .blog-read-more:hover {
              background: #273884;
              color: #fff;
            }
             .blog-read-more:hover i{ 
              color: #fff;
            }

            .blog-views {
              display: inline-flex;
              align-items: center;
              gap: 7px;
              color: #777;
              font-size: 14px;
              padding-right: 16px;
            }

            .blog-empty {
              color: #777;
              text-align: center;
              padding: 36px 0;
            }

            .blog-load-more {
              text-align: center;
              margin-top: 30px;
            }

            .blog-load-more .tf-btn-full {
              width: auto;
              min-width: 160px;
            }

            @media (max-width: 991px) {
              .blog-news-page {
                padding: 18px 16px 56px;
              }

              .blog-breadcrumb {
                margin-bottom: 26px;
              }

              .blog-dot-grid {
                left: -18px;
              }

              .blog-intro-card {
                padding: 24px 22px 34px;
              }

              .blog-intro-card h1 {
                font-size: 30px;
              }

              .blog-filter-row {
                margin-left: 0;
              }

              .blog-list-item {
                grid-template-columns: 1fr;
                gap: 16px;
              }

              .blog-list-content {
                padding-top: 0;
              }

              .blog-list-content h2 {
                font-size: 24px;
                margin-bottom: 14px;
              }

              .blog-meta {
                margin-bottom: 16px;
              }

              .blog-list-footer {
                margin-top: 24px;
              }
            }

            @media (max-width: 575px) {
              .blog-news-page {
                padding-left: 14px;
                padding-right: 14px;
              }

              .blog-intro-card {
                border-radius: 12px;
                padding: 22px 18px 28px;
              }

              .blog-intro-card h1 {
                font-size: 26px;
              }

              .blog-filter-row {
                align-items: flex-start;
                flex-direction: column;
                gap: 8px;
                margin-bottom: 28px;
              }

              .blog-filter-row select {
                width: 100%;
              }

              .blog-list-content h2 {
                font-size: 22px;
              }

              .blog-list-footer {
                align-items: flex-start;
                flex-direction: column;
              }

              .blog-views {
                padding-right: 0;
              }
            }
          `,
        }}
      />
      <CTA />
    </>
  );
}

function getAuthor(item) {
  return (
    item.agentName ||
    item.AgentName ||
    item.author ||
    item.Author ||
    item.postedby ||
    item.PostedBy ||
    item.createdby ||
    item.CreatedBy ||
    "NV Real Estate"
  );
}

function getInitials(name) {
  const initials = String(name || "NV")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part.charAt(0))
    .join("");

  return initials || "NV";
}

function formatDate(value) {
  if (!value) {
    return "";
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleDateString("en-GB", {
    weekday: "long",
    day: "2-digit",
    month: "long",
    year: "numeric",
  });
}

export default Blogs;
